import React, { useRef, useEffect, useState } from "react";
import "./student.css";
import { Outlet, useNavigate } from "react-router-dom";
import Logo from "../../../ui/atoms/logo/Logo";
import Button from "../../../ui/atoms/buttons/Button";
import Notification from "../../../ui/atoms/notification/Notification";
import Profile from "../../../ui/atoms/profile/Profile";
import ASSETS from "../../../app/config/assets";
import { ReactComponent as AdmissionTestImage } from "../../../assets/images/admission-test.svg";
import { ReactComponent as SubscriptionImage } from "../../../assets/images/subscription.svg";
import NavLinkTab from "../../../ui/molecules/navlink-tab/NavLinkTab";
import { useFullscreen } from "../../../app/providers/useFullscreen";
import { AiOutlineFullscreen, AiOutlineFullscreenExit } from "react-icons/ai";
import { RiDashboardHorizontalLine } from "react-icons/ri";
import { MdOutlineAssessment } from "react-icons/md";
import { SlBookOpen } from "react-icons/sl";
import { GoCommentDiscussion } from "react-icons/go";
import { FaPeopleLine } from "react-icons/fa6";
import { useSelector, useDispatch } from "react-redux";
import {
  capitalize,
  capitalizeAll,
  isEmpty,
  wait,
} from "../../../core/utils/helpers";
import { configurationSlice } from "../../../app/store/slices/configurationSlice";
import { challengeSlice } from "../../../app/store/slices/challengeSlice";
import { socketIo } from "../../../core/socket/socket-client";
import {
  onGetAdmissionTestStatus,
  onCreateStudentAdmissionTest,
} from "../services/classTest";
import { onGetClassmates } from "../services/school";
import {
  onGetNotifications,
  onGetDiscussions,
} from "../../admin/services/user";
import { onGetMe } from "../../authenticate/services/authentication";
import moment from "moment";
import Swal from "sweetalert2";
import useAxiosPrivate from "../../../app/providers/context-provider/useAxiosPrivate";
import useLogout from "../../../app/providers/context-provider/useLogout";

const Student = () => {
  const axiosPrivate = useAxiosPrivate();
  const wrapperRef = useRef(null);
  const { isFullscreen, enter, exit } = useFullscreen();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const logout = useLogout();

  const toggleFullscreen = () => {
    if (isFullscreen) exit();
    else enter(wrapperRef.current);
  };

  const [notifcationsAlert, setNotifcationsAlert] = useState(0);
  const [messagesAlert, setMessagesAlert] = useState(0);
  const [isClassLeaderboard, setIsClassLeaderboard] = useState(false);

  const [isAdmitted, setIsAdmitted] = useState(false);
  const [isSubsciptionExpired, setIsSubsciptionExpired] = useState(false);
  const [admissionTestData, setAdmissionTestData] = useState(null);
  const [isButtonAppear, setIsButtonAppear] = useState(false);

  const [classmatesData, setClassmatesData] = useState([]);

  const auth = useSelector((state) => state.auth);

  useEffect(() => {
    if (!auth?.accessToken) return;

    const fetchData = async () => {
      await onGetMe(axiosPrivate)
        .then((response) => {
          const _data = response?.data;
          dispatch(configurationSlice.actions.setUserInformations(_data));
          if (!_data?.is_completed) {
            navigate("/signup/complete", { replace: true });
          }
        })
        .catch((error) => {
          if (!error?.response) {
            Swal.fire({
              icon: "error",
              title: "Getting User Informations Error",
              text: "No server response",
            });
          } else {
            if (error?.response?.status === 403) {
              Swal.fire({
                icon: "info",
                title: "Session Expired",
                text: "Your session was expired. Refresh to get a new one and continue.",
              });
              return;
            }
            Swal.fire({
              icon: "warning",
              title: "Getting User Informations Warning",
              text: error?.response?.data?.message,
            });
          }
        });
    };

    fetchData();
  }, [auth?.accessToken]);

  const initConnectedUser = useSelector(
    (state) => state.setInitConf.initConnectedUser,
  );
  const _sys_role = initConnectedUser?.sys_role;

  const userInformations = useSelector(
    (state) => state.setInitConf.userInformations,
  );

  // Loading the connected class
  const currentConnectedStream = useSelector(
    (state) => state.setInitConf.currentConnectedStream,
  );
  useEffect(() => {
    if (isEmpty(currentConnectedStream?.stream_id)) {
      const { inscription } = initConnectedUser;
      dispatch(
        configurationSlice.actions.setCurrentConnectedStream(inscription),
      );
    }
  }, []);

  const _inscription_id = currentConnectedStream?.inscription_id;
  const _stream_id = currentConnectedStream?.stream_id;

  useEffect(() => {
    const fetchData = () => {
      let isMounted = true;
      const controller = new AbortController();
      const signal = controller.signal;

      const localData = {
        inscription_id: _inscription_id,
        stream_id: _stream_id,
        dates: moment(new Date()).format("YYYY-MM-DD HH:mm:ss"),
      };

      onGetAdmissionTestStatus(axiosPrivate, localData, signal)
        .then((response) => {
          const _data = response?.data;
          const currentDate = moment();
          const endDate = moment(_data?.end_subscription);
          const isGreaterOrEqual = !isEmpty(_data?.end_subscription)
            ? currentDate.isSameOrAfter(endDate)
            : false;
          setIsSubsciptionExpired(isGreaterOrEqual);
          setAdmissionTestData(_data);
        })
        .catch((error) => {
          if (!error?.response) {
            Swal.fire({
              icon: "error",
              title: "Admission Test Error",
              text: "No server response. Unable to Reach Server to get Admission Test status",
            });
          } else {
            if (error?.response?.status === 403) {
              Swal.fire({
                icon: "info",
                title: "Session Expired",
                text: "Your session was expired. Refresh to get a new one and continue.",
              });
              return;
            }
            Swal.fire({
              icon: "warning",
              title: "Admission Test Warning",
              text: error?.response?.data?.message,
            });
          }
        });

      return () => {
        isMounted = false;
        isMounted && controller.abort();
      };
    };

    if (!isEmpty(currentConnectedStream?.stream_id)) fetchData();
  }, [currentConnectedStream]);

  useEffect(() => {
    if (admissionTestData?.is_admitted) setIsAdmitted(true);
    if (admissionTestData?.questions_set_up) setIsButtonAppear(true);
  }, [admissionTestData]);

  useEffect(() => {
    if (isClassLeaderboard) {
      let isMounted = true;
      const controller = new AbortController();
      const signal = controller.signal;

      const localData = {
        inscription_id: _inscription_id,
        stream_id: _stream_id,
      };

      onGetClassmates(axiosPrivate, localData, signal)
        .then((response) => {
          const _data = response?.data;
          setClassmatesData(_data?.result);
        })
        .catch((error) => {
          if (!error?.response) {
            Swal.fire({
              icon: "error",
              title: "[MainLayout][onGetClassmates]:Error",
              text: "No server response",
            });
          } else {
            Swal.fire({
              icon: "warning",
              title: "[MainLayout][onGetClassmates]:Warning",
              text: error?.response?.data?.message,
            });
          }
        });

      return () => {
        isMounted = false;
        isMounted && controller.abort();
      };
    }
  }, [isClassLeaderboard]);

  useEffect(() => {
    onRefreshNotifications();
    onRefreshDiscussions();
  }, []);

  useEffect(() => {
    socketIo.emit("authenticate", { inscription_id: _inscription_id });

    const onHandleNewNotification = (data) => {
      if (data?.status) onRefreshNotifications();
    };

    const onHandleNewMessage = (data) => {
      if (data?.status) onRefreshDiscussions();
    };

    socketIo.on("on_new_notification", onHandleNewNotification);
    socketIo.on("on_new_message", onHandleNewMessage);

    return () => {
      socketIo.off("on_new_notification", onHandleNewNotification);
      socketIo.off("on_new_message", onHandleNewMessage);
    };
  }, []);

  const onRefreshNotifications = async () => {
    let isMounted = true;
    const controller = new AbortController();
    const signal = controller.signal;
    const _data = {
      inscription_id: _inscription_id,
      sys_role: _sys_role,
    };

    await onGetNotifications(axiosPrivate, _data, signal).then((response) => {
      const _data = response?.data;
      setNotifcationsAlert(_data?.total_overall_no_read_notifications);
    });

    return () => {
      isMounted = false;
      isMounted && controller.abort();
    };
  };

  const onRefreshDiscussions = async () => {
    const localData = {
      inscription_id: _inscription_id,
      dates: moment(new Date()).format("YYYY-MM-DD HH:mm:ss"),
    };
    await onGetDiscussions(axiosPrivate, localData).then((response) => {
      const _data = response?.data;
      setMessagesAlert(_data?.total_overall_no_read_messages);
    });
  };

  const onHandleLogout = async () => {
    const result = await Swal.fire({
      title: "Logout",
      text: "You are about to stop and quit your current session. Do you really want logout ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, I do",
    });

    if (result.isConfirmed) {
      Swal.fire({
        title:
          '<span style="font-size:1rem; color:grey;">Stopping session and logout process ongoing...</span>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,

        didOpen: () => {
          Swal.showLoading();
        },
      });

      // attendre 1.5 sec
      await wait(1500);

      // logout
      logout();

      // fermer swal si nécessaire
      Swal.close();
    }
  };

  const onHandleNewAdmissionTest = async () => {
    Swal.fire({
      title: "Admission Test questions processing...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    await wait(700);
    //
    try {
      const data = {
        inscription_id: _inscription_id,
        classes_id: _stream_id,
        dates: moment(new Date()).format("YYYY-MM-DD HH:mm:ss"),
      };

      onCreateStudentAdmissionTest(axiosPrivate, data)
        .then((response) => {
          Swal.close();
          const _data = response?.data;
          const class_admission_test_id = _data?.class_admission_test_id;
          if (_data?.status) {
            dispatch(
              challengeSlice?.actions?.setCurrentAssessment({
                mode: "AT", // AT stand for Admission Test
                admission_test_id: class_admission_test_id,
                challenge_id: null,
                challenge_type: null,
                teams_breakdown: null,
                teams_device: null,
                request_id: null,
              }),
            );
            navigate(`/${_sys_role}/challenge/play`, { replace: true });
          }
        })
        .catch((error) => {
          if (!error?.response) {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "No server response",
            });
          } else {
            Swal.fire({
              icon: "warning",
              title: "Warning",
              text: error?.response?.data?.message,
            });
          }
        });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "An error occurred while fetching data.",
      });
    }
  };

  return (
    <div className="student w-screen h-screen" ref={wrapperRef}>
      <React.Fragment>
        <div className="s-header w-full">
          <div className="s-h-container w-full d-flex">
            <div className="s-h-logo">
              <Logo to={null} width={"85px"} />
            </div>
            <div className="s-h-c-options-wrapper">
              <NavLinkTab
                tab_data={[
                  {
                    icon: RiDashboardHorizontalLine,
                    title: "dashboard",
                    nav_to: "/student",
                    is_exact: true,
                  },
                  {
                    icon: MdOutlineAssessment,
                    title: "challenges",
                    nav_to: "/student/challenges",
                    is_exact: false,
                  },
                  {
                    icon: SlBookOpen,
                    title: "courses",
                    nav_to: "/student/courses",
                    is_exact: false,
                  },
                  {
                    icon: GoCommentDiscussion,
                    title: "Discussions",
                    nav_to: "/student/discussions",
                    is_exact: false,
                  },
                ]}
              />
              <div className="s-h-c-options">
                <button onClick={toggleFullscreen}>
                  {isFullscreen ? (
                    <AiOutlineFullscreenExit className="icon" />
                  ) : (
                    <AiOutlineFullscreen className="icon" />
                  )}
                </button>
                <button
                  onClick={() => setIsClassLeaderboard(!isClassLeaderboard)}
                >
                  <FaPeopleLine className="icon" />
                </button>
                <Notification
                  icon={ASSETS?.icon_notification_png}
                  count={notifcationsAlert}
                  to={"/student/discussions"}
                />
                <Notification
                  icon={ASSETS?.icon_mail_png}
                  count={messagesAlert}
                  to={"/student/discussions/messages"}
                />
                <Profile
                  sys_role={"student"}
                  image={userInformations?.thumbnail}
                  username={`${userInformations?.firstname} ${userInformations?.lastname}`}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="s-wrapper">
          <Outlet />
        </div>
      </React.Fragment>
      {/* Classmates Section */}
      <div
        className={
          isClassLeaderboard
            ? "classmates-stage slide-in"
            : "classmates-stage slide-out"
        }
      >
        <div className="classmates-stage-head">
          <div>
            <h1>Classmates and Class Leaderboard</h1>
            <p>
              See your Class Leaderboard and Classmates who is online for group
              studying, discussion or challenges
            </p>
          </div>
          <Button
            type={"button"}
            preferedStyle={"default"}
            title={"Close"}
            action={() => setIsClassLeaderboard(!isClassLeaderboard)}
          />
        </div>
        <div className="classmates-stage-body">
          {classmatesData?.map((classmate) => {
            return (
              <div className="classmate-item" key={classmate?.inscription_id}>
                <span className="span-place">{classmate?.ranking}</span>
                <div className="classmate-profile">
                  <img
                    src={process.env.PUBLIC_URL + "/user.png"}
                    alt="classmate profile"
                  />
                  <div className="col">
                    <h2>
                      {capitalizeAll(
                        `${classmate?.firstname} ${classmate?.lastname}`,
                      )}
                    </h2>
                    <p>{capitalizeAll(classmate?.origin_country)}</p>
                  </div>
                </div>
                <div className="c-col">
                  <span>Grade</span>
                  <p>{classmate?.grade}</p>
                </div>
                <div className="c-col">
                  <span>Quiz</span>
                  <p>{classmate?.quiz}</p>
                </div>
                <div className="c-col red">
                  <span>Assessment</span>
                  <p>{classmate?.assessment}</p>
                </div>
                <div className="c-col grey">
                  <span>Laboratory</span>
                  <p>{classmate?.laboratory}</p>
                </div>
                {!classmate?.is_me && (
                  <>
                    <div
                      className={`c-col  ${classmate?.is_online ? "green" : "red"}`}
                    >
                      <span>State</span>
                      <p>{`${classmate?.is_online ? "online" : "offline"}`}</p>
                    </div>
                    <button
                      onClick={() => {
                        dispatch(
                          configurationSlice.actions.setCurrentConversation({
                            participant_id: classmate?.inscription_id,
                            participant_name: `${classmate?.firstname} ${classmate?.lastname}`,
                            type: "P2P",
                            connexion_status: classmate?.is_online,
                          }),
                        );
                        navigate(`/${_sys_role}/discussions/messages`);
                        setIsClassLeaderboard(false);
                      }}
                    >
                      Click to Contact
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {!isAdmitted && (
        <div className="admission-box fade-in">
          <div className="a-b-container">
            <AdmissionTestImage className="a-b-image" />
            <h1>
              {`👋🏼 ${capitalize(userInformations?.firstname)} ${capitalize(
                userInformations?.lastname,
              )}, we’re delighted to welcome you to Maarifa University.`}
            </h1>
            {admissionTestData?.questions_set_up ? (
              <p>
                Before you get access to your class, you need to take and
                validate an compulsory admission test with{" "}
                <span>a minimum of 50%</span>.
              </p>
            ) : (
              <>
                <h3>
                  😥 The question set for your stream's admission test is not
                  ready yet. The team is still working on it, please come back
                  later!
                </h3>
                <div className="w-fit-content p-lg">
                  <Button
                    type={"button"}
                    preferedStyle={"default"}
                    title={"Click to Logout"}
                    action={() => onHandleLogout()}
                  />
                </div>
              </>
            )}
            {isButtonAppear && (
              <div className="w-fit-content p-lg">
                <Button
                  type={"button"}
                  preferedStyle={"normal"}
                  title={"Take my Admission Test now ➡️"}
                  action={() => onHandleNewAdmissionTest()}
                />
              </div>
            )}
            <div className="w-fit-content d-flex flex-col align-center p-md">
              <p>
                If you have any questions, please contact the Administration :
              </p>
              <span>☎️ +243 99 14 20 982</span>
              <span>📧 contact@pamoja4mi.io</span>
            </div>
          </div>
        </div>
      )}
      {isSubsciptionExpired && (
        <div className="admission-box fade-in">
          <div className="a-b-container">
            <SubscriptionImage className="a-b-image" />
            <div className="w-full d-flex flex-col align-center p-lg">
              <h1>
                {`${
                  userInformations?.gender === "male" ? "Mr" : "Mme"
                }. ${capitalize(userInformations?.firstname)} ${capitalize(
                  userInformations?.lastname,
                )}, 👋🏼 Welcome back to Pamoja4MI University.`}
              </h1>
              <h3 style={{ textAlign: "center" }}>
                😥 "Sorry, you can't access your class! Your subscription ended{" "}
                {moment(admissionTestData?.end_subscription).fromNow()}, so
                you're unable to join your classmates.
              </h3>
              <p>
                Don't worry, your data is safe. Renew your subscription now to
                jump back into the action!"
              </p>
            </div>
            <div className="w-full d-flex flex-col align-center p-lg gap-md">
              <div className="w-fit-content">
                <Button
                  type={"button"}
                  preferedStyle={"normal"}
                  title={"Get NEW SUBSCRIPTION now ➡️"}
                  action={() => {}}
                />
              </div>
              <span>or</span>
              <div className="w-fit-content">
                <Button
                  type={"button"}
                  preferedStyle={"default"}
                  title={"Click to Logout"}
                  action={() => onHandleLogout()}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Student;
